import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Component, inject, OnInit, TemplateRef, ViewChild, ChangeDetectorRef, effect, untracked, DestroyRef } from "@angular/core";
import { LangChangeEvent, TranslateService, TranslatePipe } from "@ngx-translate/core";
import { componentApi, JumpstartComponentsWkIconsModule, JumpstartComponentsModule } from "@wk/components-v3-angular17";

import {
    AuthService,
    CardViewService,
    DashboardsService,
    FilterSortModalHandlerService,
    FiltersService,
    HelpersService,
    LocalStorageService,
    ResponseErrorHandlerService,
    IFirmGroupsService,
    AnalyticsService,
    MANAGER_ROLE_ID,
    PARTNER_ROLE_ID,
} from "../shared";

import { AlertsFilterGeneratorService } from "./alerts-filter-generator.service";
import { AssigneesFilterGeneratorService } from "./assignees-role-filter-generator.service";
import { FavoriteFilters } from "./favorite-filter-model";
import { MultiSelectModel, Option, SingleSelectModel, DateInputModel } from "@app/filter-menu/filter-types";
import { IfirmGroupFilterGeneratorService } from "./ifirm-group-filter-generator.service";
import { JobsGroupFilterGeneratorService } from "./jobs-group-filter-generator.service";
import { JobsTypeFilterGeneratorService } from "./jobs-type-filter-generator.service";
import { UsersFilterGeneratorService } from "./users-filter-generator.service";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { PendingChangesPopupComponent } from "../shared/pending-changes-popup/pending-changes-popup.component";
import { TargetStartFilterGeneratorService } from "./target-start-filter-generator.service";
import { TargetEndFilterGeneratorService } from "./target-end-filter-generator.service";
import { PeriodEndFilterGeneratorService } from "./period-end-filter-generator.service";
import { PriorityFilterGeneratorService } from "./priority-filter-generator.service";
import { TargetStartFromFilterGeneratorService } from "./target-start-from-filter-generator.service";
import { TargetStartToFilterGeneratorService } from "./target-start-to-filter-generator.service";
import { DueOnFilterGeneratorService } from "./due-on-filter-generator.service";
import { JobsPartnerFilterGeneratorService } from "./jobs-partner-filter-generator.service";
import { JobsManagerFilterGeneratorService } from "./jobs-manager-filter-generator.service";
import { JobsOwnerFilterGeneratorService } from "./jobs-owner-filter-generator.service";
import { JobsCurrentlyResponsibleFilterGeneratorService } from "./jobs-currentlyresposible-filter-generator.service";
import { IJobsCurrentSettings } from "@app/shared/interfaces/jobs-current-settings-response.interface";
import { JobsAndBillingCommonService } from "@app/shared/jobs-and-billing-common-service/jobs-and-billing-common.service";
import { SearchComponent } from "../search-component/search.component";
import { KendoMultiselectWrapper } from "../shared/components/kendo-multiselect-wrapper/kendo-multiselect-wrapper.component";
import { FormsModule } from "@angular/forms";
import { KendoDropDownListWrapper } from "../shared/components/kendo-dropdownlist-wrapper/kendo-dropdownlist-wrapper.component";
import { TargetStartDatesComponent } from "./target-start-dates/target-start-dates.component";
import { TargetEndDatesComponent } from "./target-end-dates/target-end-dates.component";
import { PeriodEndDatesComponent } from "./period-end-dates/period-end-dates.component";
import { DatePipe } from "@angular/common";
import { WkTranslatePipe } from "../shared/utils/wk-translate-pipe/wk-translate.pipe";

@Component({
    selector: "wk-jobs-filter-menu",
    templateUrl: "./jobs-filter-menu.component.html",
    styleUrls: ["../filter-menu/filter-menu.component.scss", "../filter-menu/filter-modals.scss"],
    host: { class: "wk-fm" },
    providers: [
        AlertsFilterGeneratorService,
        AssigneesFilterGeneratorService,
        PriorityFilterGeneratorService,
        IfirmGroupFilterGeneratorService,
        JobsGroupFilterGeneratorService,
        JobsTypeFilterGeneratorService,
        UsersFilterGeneratorService,
        TargetStartFilterGeneratorService,
        TargetEndFilterGeneratorService,
        PeriodEndFilterGeneratorService,
        TargetStartFromFilterGeneratorService,
        TargetStartToFilterGeneratorService,
        DueOnFilterGeneratorService,
        JobsPartnerFilterGeneratorService,
        JobsManagerFilterGeneratorService,
        JobsCurrentlyResponsibleFilterGeneratorService,
        JobsOwnerFilterGeneratorService,
    ],
    imports: [
        JumpstartComponentsWkIconsModule,
        JumpstartComponentsModule,
        SearchComponent,
        KendoMultiselectWrapper,
        FormsModule,
        KendoDropDownListWrapper,
        TargetStartDatesComponent,
        TargetEndDatesComponent,
        PeriodEndDatesComponent,
        DatePipe,
        WkTranslatePipe,
        TranslatePipe,
    ],
})
export class JobsFilterMenuComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly alertsFilterGeneratorService = inject(AlertsFilterGeneratorService);
    protected readonly assigneesFilterGeneratorService = inject(AssigneesFilterGeneratorService);
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly helpersService = inject(HelpersService);
    private readonly ifirmGroupFilterGeneratorService = inject(IfirmGroupFilterGeneratorService);
    private readonly jobsGroupFilterGeneratorService = inject(JobsGroupFilterGeneratorService);
    private readonly jobsTypeFilterGeneratorService = inject(JobsTypeFilterGeneratorService);
    private readonly usersFilterGeneratorService = inject(UsersFilterGeneratorService);
    private readonly jobsPartnerFilterGeneratorService = inject(JobsPartnerFilterGeneratorService);
    private readonly jobsManagerFilterGeneratorService = inject(JobsManagerFilterGeneratorService);
    private readonly jobsCurrentlyResponsibleFilterGeneratorService = inject(JobsCurrentlyResponsibleFilterGeneratorService);
    private readonly jobsOwnerFilterGeneratorService = inject(JobsOwnerFilterGeneratorService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly modalCoordinationService = inject(FilterSortModalHandlerService);
    public readonly filtersService = inject(FiltersService);
    private readonly ifirmGroupsService = inject(IFirmGroupsService);
    private readonly targetStartFilterGeneratorService = inject(TargetStartFilterGeneratorService);
    private readonly targetEndFilterGeneratorService = inject(TargetEndFilterGeneratorService);
    private readonly periodEndFilterGeneratorService = inject(PeriodEndFilterGeneratorService);
    private readonly priorityFilterGeneratorService = inject(PriorityFilterGeneratorService);
    private readonly targetStartFromFilterGeneratorService = inject(TargetStartFromFilterGeneratorService);
    private readonly targetStartToFilterGeneratorService = inject(TargetStartToFilterGeneratorService);
    private readonly dueOnFilterGeneratorService = inject(DueOnFilterGeneratorService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);

    alertsFilterModel: MultiSelectModel;
    priorityFilterModel: MultiSelectModel;
    group1FilterModel: MultiSelectModel;
    group2FilterModel: MultiSelectModel;
    jobsGroup1FilterModel: MultiSelectModel | undefined;
    jobsGroup2FilterModel: MultiSelectModel | undefined;
    jobsTypeFilterModel: MultiSelectModel;
    usersFilterModel: MultiSelectModel;
    currentlyResponsibleFilterModel: MultiSelectModel;
    jobPartnerFilterModel: MultiSelectModel;
    jobManagerFilterModel: MultiSelectModel;
    jobOwnerFilterModel: MultiSelectModel;
    targetStartFilterModel: SingleSelectModel;
    targetStartFromFilterModel: DateInputModel;
    targetStartToFilterModel: DateInputModel;
    targetStartDueOnFilterModel: DateInputModel;
    targetEndFilterModel: SingleSelectModel;
    targetEndFromFilterModel: DateInputModel;
    targetEndToFilterModel: DateInputModel;
    targetEndDueOnFilterModel: DateInputModel;
    periodEndFilterModel: SingleSelectModel;
    periodEndFromFilterModel: DateInputModel;
    periodEndToFilterModel: DateInputModel;
    periodEndDueOnFilterModel: DateInputModel;
    isOpen!: boolean;
    accordionStates: Record<string, boolean> = {};
    headingId = "";
    linesCount = 3;
    isExpanded = false;
    morePillsCount = 0;

    public changeDetector = inject(ChangeDetectorRef);
    writeState = componentApi.writeState.bind(this);
    favoritesFilters: FavoriteFilters[] = [];
    currentFavoriteFilter: FavoriteFilters | null = {
        id: "",
        name: "",
        active: false,
        selections: {},
    };

    isFavoriteFilterEditMode = false;
    hasPendingFavoriteFilterChanges = false;
    modalActionType: "save" | "rename" = "save";
    favoriteFiltersLocalStorageKey = "";
    pendingFavoriteToApply: FavoriteFilters | null = null;
    isGroup1Enabled = false;
    isGroup2Enabled = false;
    @ViewChild("saveFilterDialogue") saveFilterDialogue!: TemplateRef<any>;
    @ViewChild("deleteFilterDialogue") deleteFilterDialogue!: TemplateRef<any>;

    bsModalRef?: BsModalRef;
    protected jobsAndBillingCurrentSettings: IJobsCurrentSettings;
    protected currentLanguage = "en-CA";
    protected readonly datePillFormat = "yyyy-MM-dd";
    protected filteredJobsTypeOptions: any[] | undefined;
    protected filteredGroup1Options: any[] | undefined;
    protected filteredGroup2Options: any[] | undefined;
    // Suppresses the assignees-effect handleFilterChange() call while clearAllFilters/applyFavoriteFilter run, to fire it once at the end.
    private isBulkFilterUpdate = false;

    constructor() {
        effect(() => {
            const models = this.assigneesFilterGeneratorService.models();
            if (!models || models.length === 0) return;

            untracked(() => {
                models.forEach((model) => {
                    this.setAssigneeFilter(model);
                });
                if (!this.isBulkFilterUpdate) {
                    this.filtersService.handleFilterChange();
                }
            });
        });

        // Model signals republish whenever selection, users, or cards data change.
        effect(() => {
            const model = this.usersFilterGeneratorService.model();
            if (!model) return;
            untracked(() => {
                this.usersFilterModel = model;
                this.setUsersFilter(model.selection);
                if (!this.isBulkFilterUpdate) {
                    this.filtersService.handleFilterChange();
                }
            });
        });

        effect(() => {
            const model = this.jobsCurrentlyResponsibleFilterGeneratorService.model();
            if (!model) return;
            untracked(() => {
                this.currentlyResponsibleFilterModel = model;
                this.setCurrentlyResponsibleFilter(model.selection);
                if (!this.isBulkFilterUpdate) {
                    this.filtersService.handleFilterChange();
                }
            });
        });

        effect(() => {
            const model = this.jobsPartnerFilterGeneratorService.model();
            if (!model) return;
            untracked(() => {
                this.jobPartnerFilterModel = model;
                this.setJobPartnerFilter(model.selection);
                if (!this.isBulkFilterUpdate) {
                    this.filtersService.handleFilterChange();
                }
            });
        });

        effect(() => {
            const model = this.jobsManagerFilterGeneratorService.model();
            if (!model) return;
            untracked(() => {
                this.jobManagerFilterModel = model;
                this.setJobManagerFilter(model.selection);
                if (!this.isBulkFilterUpdate) {
                    this.filtersService.handleFilterChange();
                }
            });
        });

        effect(() => {
            const model = this.jobsOwnerFilterGeneratorService.model();
            if (!model) return;
            untracked(() => {
                this.jobOwnerFilterModel = model;
                this.setJobOwnerFilter(model.selection);
                if (!this.isBulkFilterUpdate) {
                    this.filtersService.handleFilterChange();
                }
            });
        });

        this.currentLanguage = this.translateService.getCurrentLang()
            ? this.translateService.getCurrentLang()
            : this.translateService.getFallbackLang();
        this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang;
        });
    }

    async ngOnInit() {
        this.setupSubscriptions();
        this.isOpen = false;
        this.accordionStates["Accordion_Filters_General"] = this.getAccordionState("Accordion_Filters_General");
        this.accordionStates["Accordion_Filters_Jobs"] = this.getAccordionState("Accordion_Filters_Jobs");
        this.accordionStates["Accordion_Filters_ContactDetails"] = this.getAccordionState("Accordion_Filters_ContactDetails");
        this.accordionStates["Accordion_Filters_Dates"] = this.getAccordionState("Accordion_Filters_Dates");
        this.accordionStates["Accordion_Filters_Active"] = this.getAccordionState("Accordion_Filters_Active");
        this.accordionStates["Accordion_Filters_Favorites"] = this.getAccordionState("Accordion_Filters_Favorites");
        this.initializeFilters();
        this.jobsAndBillingCurrentSettings = await this.jobsAndBillingCommonService.getCurrentSettings();
    }

    private setupSubscriptions(): void {
        this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {});
        this.modalCoordinationService.activeModal$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeModal) => {
            if (activeModal !== "filter" && activeModal !== "pendingChanges") {
                this.isOpen = false;
            }
            if (activeModal === "pendingChanges" && this.hasPendingFavoriteFilterChanges) {
                this.showPendingChangesPopup(() => {
                    this.isOpen = false;
                    this.modalCoordinationService.closeAllModals();
                    this.modalCoordinationService.setActiveModal("sort");
                });
            }
        });
        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (currentDashboardId) => {
            this.isOpen = false;
            this.isFavoriteFilterEditMode = false;
            this.hasPendingFavoriteFilterChanges = false;
            this.clearCurrentFavoriteFilter();
            const item = localStorage.getItem(`Filter_Selection_${currentDashboardId}`);
            const storedSelection = item && item !== "" ? JSON.parse(item) : {};
            this.favoriteFiltersLocalStorageKey = this.getFavoritesFiltersKey(currentDashboardId, this.authService.currentUser()?.sub);
            await this.loadFilters(storedSelection);
            this.loadFavoriteFilters();
            this.applyActiveFavoriteOnLoad();
            await this.loadGroupEnabledState();
        });
        this.cardViewService.viewTypeChangeEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (viewType) => {
            if (this.alertsFilterModel) {
                this.alertsFilterModel = this.alertsFilterGeneratorService.generateModel(this.alertsFilterModel.selection, viewType);
                this.onAlertsChanged(this.alertsFilterModel.selection);
            }
        });
        this.helpersService.favouriteFiltersEditModeEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
            this.handleFilterEditMode(event);
        });
        this.helpersService.favouriteFiltersPendingChangesEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
            this.hasPendingFavoriteFilterChanges = event;
        });
        this.helpersService.revertToFavoriteFilterEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.currentFavoriteFilter?.id) {
                // Disable edit mode before applying to prevent triggering pending changes
                this.isFavoriteFilterEditMode = false;
                this.applyFavoriteFilter(this.currentFavoriteFilter);
            }
        });
    }

    private async initializeFilters(): Promise<void> {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        await this.loadGroupEnabledState();
        const item = localStorage.getItem(`Filter_Selection_${currentDashboard.Id}`);
        const storedSelection = item && item !== "" ? JSON.parse(item) : {};
        this.favoriteFiltersLocalStorageKey = this.getFavoritesFiltersKey(currentDashboard.Id, this.authService.currentUser()?.sub);
        await this.loadFilters(storedSelection);
        this.loadFavoriteFilters();
        this.applyActiveFavoriteOnLoad();
    }

    async loadFilters(storedSelection) {
        const viewType = this.cardViewService.currentViewType;

        this.alertsFilterModel = this.alertsFilterGeneratorService.generateModel(storedSelection.Alerts ?? [], viewType);
        this.priorityFilterModel = this.priorityFilterGeneratorService.generateModel(storedSelection.Priority ?? []);
        this.group1FilterModel = await this.ifirmGroupFilterGeneratorService.generateGroup1Model(storedSelection.Group1 ?? []);
        this.group2FilterModel = await this.ifirmGroupFilterGeneratorService.generateGroup2Model(storedSelection.Group2 ?? []);
        this.jobsGroup1FilterModel = await this.jobsGroupFilterGeneratorService.generateGroup1Model(storedSelection.JobsGroup1 ?? []);
        this.filteredGroup1Options = this.jobsGroup1FilterModel?.applicableOptions ? [...this.jobsGroup1FilterModel.applicableOptions] : undefined;
        this.jobsGroup2FilterModel = await this.jobsGroupFilterGeneratorService.generateGroup2Model(storedSelection.JobsGroup2 ?? []);
        this.filteredGroup2Options = this.jobsGroup2FilterModel?.applicableOptions ? [...this.jobsGroup2FilterModel.applicableOptions] : undefined;
        this.jobsTypeFilterModel = await this.jobsTypeFilterGeneratorService.generateModel(storedSelection.JobsType ?? []);
        this.filteredJobsTypeOptions = this.jobsTypeFilterModel?.applicableOptions ? [...this.jobsTypeFilterModel.applicableOptions] : undefined;
        this.usersFilterGeneratorService.loadModel(storedSelection.Users ?? []);
        this.jobsCurrentlyResponsibleFilterGeneratorService.loadModel(storedSelection.CurrentlyResponsible ?? []);
        this.jobsPartnerFilterGeneratorService.loadModel(storedSelection.JobPartner ?? []);
        this.jobsManagerFilterGeneratorService.loadModel(storedSelection.JobManager ?? []);
        this.jobsOwnerFilterGeneratorService.loadModel(storedSelection.JobOwner ?? []);
        this.targetStartFilterModel = this.targetStartFilterGeneratorService.generateModel(storedSelection.TargetStart ?? "");
        this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(storedSelection.TargetStartFrom ?? "");
        this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel(storedSelection.TargetStartTo ?? "");
        this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(storedSelection.TargetStartDueOn ?? "");
        this.targetEndFilterModel = this.targetEndFilterGeneratorService.generateModel(storedSelection.TargetEnd ?? "");
        this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(storedSelection.TargetEndFrom ?? "");
        this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(storedSelection.TargetEndTo ?? "");
        this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(storedSelection.TargetEndDueOn ?? "");
        this.periodEndFilterModel = this.periodEndFilterGeneratorService.generateModel(storedSelection.PeriodEnd ?? "");
        this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(storedSelection.PeriodEndFrom ?? "");
        this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(storedSelection.PeriodEndTo ?? "");
        this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(storedSelection.PeriodEndDueOn ?? "");
        this.assigneesFilterGeneratorService.loadModels(storedSelection.Assignees);

        this.setAlertsFilter(this.alertsFilterModel.selection);
        this.setPriorityFilter(this.priorityFilterModel.selection);
        await this.setGroup1Filter(this.group1FilterModel.selection);
        await this.setGroup2Filter(this.group2FilterModel.selection);
        if (this.jobsGroup1FilterModel) await this.setJobsGroup1Filter(this.jobsGroup1FilterModel.selection);
        if (this.jobsGroup2FilterModel) await this.setJobsGroup2Filter(this.jobsGroup2FilterModel.selection);
        await this.setJobsTypeFilter(this.jobsTypeFilterModel.selection);
        await this.setTargetStartFilter(this.targetStartFilterModel.selection.value as string);
        await this.setTargetEndFilter(this.targetEndFilterModel.selection.value as string);
        await this.setPeriodEndFilter(this.periodEndFilterModel.selection.value as string);
        this.filtersService.handleFilterChange();
    }

    async onAlertsChanged(selectedOptions: Option[], trackAnalytics = false) {
        this.handleFavoriteFilterOnSelectionChange("alerts", selectedOptions, trackAnalytics);
        await this.storeAndSetAlertsFilter(selectedOptions);
        this.filtersService.handleFilterChange(trackAnalytics);
    }

    async storeAndSetAlertsFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Alerts"], selectedOptions);
        this.setAlertsFilter(selectedOptions);
    }

    setAlertsFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = this.alertsFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Alerts");
            this.filtersService.activeFiltersForPrint.delete("Alerts");
        } else {
            this.filtersService.activeFilters.set("Alerts", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Alerts",
                this.alertsFilterGeneratorService.getPrintDescription(this.alertsFilterModel.label, selectedOptions),
            );
        }
    }

    async onPriorityChanged(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("priority", selectedOptions);
        await this.storeAndSetPriorityFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetPriorityFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Priority"], selectedOptions);
        this.setPriorityFilter(selectedOptions);
    }

    setPriorityFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = this.priorityFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Priority");
            this.filtersService.activeFiltersForPrint.delete("Priority");
        } else {
            this.filtersService.activeFilters.set("Priority", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Priority",
                this.priorityFilterGeneratorService.getPrintDescription(this.priorityFilterModel.label, selectedOptions),
            );
        }
    }

    async onGroup1Changed(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("group1", selectedOptions);
        await this.storeAndSetGroup1Filter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetGroup1Filter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Group1"], selectedOptions);
        await this.setGroup1Filter(selectedOptions);
    }

    async setGroup1Filter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = await this.ifirmGroupFilterGeneratorService.generateGroup1Filter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Group1");
            this.filtersService.activeFiltersForPrint.delete("Group1");
        } else {
            this.filtersService.activeFilters.set("Group1", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Group1",
                this.ifirmGroupFilterGeneratorService.getPrintDescription(this.group1FilterModel.label, selectedOptions),
            );
        }
    }

    async onGroup2Changed(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("group2", selectedOptions);
        await this.storeAndSetGroup2Filter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetGroup2Filter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Group2"], selectedOptions);
        await this.setGroup2Filter(selectedOptions);
    }

    async setGroup2Filter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = await this.ifirmGroupFilterGeneratorService.generateGroup2Filter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Group2");
            this.filtersService.activeFiltersForPrint.delete("Group2");
        } else {
            this.filtersService.activeFilters.set("Group2", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Group2",
                this.ifirmGroupFilterGeneratorService.getPrintDescription(this.group2FilterModel.label, selectedOptions),
            );
        }
    }

    async onJobsGroup1Changed(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("jobsGroup1", selectedOptions);
        await this.storeAndSetJobsGroup1Filter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetJobsGroup1Filter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["JobsGroup1"], selectedOptions);
        await this.setJobsGroup1Filter(selectedOptions);
    }

    async setJobsGroup1Filter(selectedOptions: Option[]) {
        if (!this.jobsGroup1FilterModel) return;
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = await this.jobsGroupFilterGeneratorService.generateGroup1Filter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("JobsGroup1");
            this.filtersService.activeFiltersForPrint.delete("JobsGroup1");
        } else {
            this.filtersService.activeFilters.set("JobsGroup1", filter);
            this.filtersService.activeFiltersForPrint.set(
                "JobsGroup1",
                this.jobsGroupFilterGeneratorService.getPrintDescription(this.jobsGroup1FilterModel.label, selectedOptions),
            );
        }
    }

    async onJobsGroup2Changed(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("jobsGroup2", selectedOptions);
        await this.storeAndSetJobsGroup2Filter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetJobsGroup2Filter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["JobsGroup2"], selectedOptions);
        await this.setJobsGroup2Filter(selectedOptions);
    }

    async setJobsGroup2Filter(selectedOptions: Option[]) {
        if (!this.jobsGroup2FilterModel) return;
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = await this.jobsGroupFilterGeneratorService.generateGroup2Filter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("JobsGroup2");
            this.filtersService.activeFiltersForPrint.delete("JobsGroup2");
        } else {
            this.filtersService.activeFilters.set("JobsGroup2", filter);
            this.filtersService.activeFiltersForPrint.set(
                "JobsGroup2",
                this.jobsGroupFilterGeneratorService.getPrintDescription(this.jobsGroup2FilterModel.label, selectedOptions),
            );
        }
    }

    async onJobsTypeChanged(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("jobsType", selectedOptions);
        await this.storeAndSetJobsTypeFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetJobsTypeFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["JobsType"], selectedOptions);
        await this.setJobsTypeFilter(selectedOptions);
    }

    async setJobsTypeFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = await this.jobsTypeFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("JobsType");
            this.filtersService.activeFiltersForPrint.delete("JobsType");
        } else {
            this.filtersService.activeFilters.set("JobsType", filter);
            this.filtersService.activeFiltersForPrint.set(
                "JobsType",
                this.jobsTypeFilterGeneratorService.getPrintDescription(this.jobsTypeFilterModel.label, selectedOptions),
            );
        }
    }

    async onUsersChanged(selectedOptions: Option[]) {
        this.usersFilterGeneratorService.updateSelection(selectedOptions);
        this.handleFavoriteFilterOnSelectionChange("users", selectedOptions);
        await this.storeAndSetUsersFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetUsersFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Users"], selectedOptions);
        this.setUsersFilter(selectedOptions);
    }

    setUsersFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value);
        const filter = this.usersFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Users");
            this.filtersService.activeFiltersForPrint.delete("Users");
        } else {
            this.filtersService.activeFilters.set("Users", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Users",
                this.usersFilterGeneratorService.getPrintDescription(this.usersFilterModel.label, selectedOptions),
            );
        }
    }

    async onCurrentlyResponsibleChanged(selectedOptions: Option[]) {
        this.jobsCurrentlyResponsibleFilterGeneratorService.updateSelection(selectedOptions);
        this.handleFavoriteFilterOnSelectionChange("currentlyResponsible", selectedOptions);
        await this.storeAndSetCurrentlyResponsibleFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetCurrentlyResponsibleFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["CurrentlyResponsible"], selectedOptions);
        this.setCurrentlyResponsibleFilter(selectedOptions);
    }

    setCurrentlyResponsibleFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value);
        const filter = this.jobsCurrentlyResponsibleFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("CurrentlyResponsible");
            this.filtersService.activeFiltersForPrint.delete("CurrentlyResponsible");
        } else {
            this.filtersService.activeFilters.set("CurrentlyResponsible", filter);
            this.filtersService.activeFiltersForPrint.set(
                "CurrentlyResponsible",
                this.jobsCurrentlyResponsibleFilterGeneratorService.getPrintDescription(this.currentlyResponsibleFilterModel.label, selectedOptions),
            );
        }
    }

    async onJobPartnerChanged(selectedOptions: Option[]) {
        this.jobsPartnerFilterGeneratorService.updateSelection(selectedOptions);
        this.handleFavoriteFilterOnSelectionChange("jobPartner", selectedOptions);
        await this.storeAndSetJobPartnerFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async onJobManagerChanged(selectedOptions: Option[]) {
        this.jobsManagerFilterGeneratorService.updateSelection(selectedOptions);
        this.handleFavoriteFilterOnSelectionChange("jobManager", selectedOptions);
        await this.storeAndSetJobManagerFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async onJobOwnerChanged(selectedOptions: Option[]) {
        this.jobsOwnerFilterGeneratorService.updateSelection(selectedOptions);
        this.handleFavoriteFilterOnSelectionChange("jobOwner", selectedOptions);
        await this.storeAndSetJobOwnerFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetJobPartnerFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["JobPartner"], selectedOptions);
        this.setJobPartnerFilter(selectedOptions);
    }

    async storeAndSetJobManagerFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["JobManager"], selectedOptions);
        this.setJobManagerFilter(selectedOptions);
    }

    async storeAndSetJobOwnerFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["JobOwner"], selectedOptions);
        this.setJobOwnerFilter(selectedOptions);
    }

    setJobPartnerFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value);
        const filter = this.jobsPartnerFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("JobPartner");
            this.filtersService.activeFiltersForPrint.delete("JobPartner");
        } else {
            this.filtersService.activeFilters.set("JobPartner", filter);
            this.filtersService.activeFiltersForPrint.set(
                "JobPartner",
                this.jobsPartnerFilterGeneratorService.getPrintDescription(this.jobPartnerFilterModel.label, selectedOptions),
            );
        }
    }

    setJobManagerFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value);
        const filter = this.jobsManagerFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("JobManager");
            this.filtersService.activeFiltersForPrint.delete("JobManager");
        } else {
            this.filtersService.activeFilters.set("JobManager", filter);
            this.filtersService.activeFiltersForPrint.set(
                "JobManager",
                this.jobsManagerFilterGeneratorService.getPrintDescription(this.jobManagerFilterModel.label, selectedOptions),
            );
        }
    }

    setJobOwnerFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value);
        const filter = this.jobsOwnerFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("JobOwner");
            this.filtersService.activeFiltersForPrint.delete("JobOwner");
        } else {
            this.filtersService.activeFilters.set("JobOwner", filter);
            this.filtersService.activeFiltersForPrint.set(
                "JobOwner",
                this.jobsOwnerFilterGeneratorService.getPrintDescription(this.jobOwnerFilterModel.label, selectedOptions),
            );
        }
    }

    async onTargetStartChanged(selectedOption: string) {
        this.targetStartFilterModel.selection =
            this.targetStartFilterModel.applicableOptions.find((option) => option.value === selectedOption) ??
            this.targetStartFilterModel.applicableOptions[0];
        this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.handleFavoriteFilterOnSelectionChange("targetStart", selectedOption);
        await this.storeAndSetTargetStartFilter();
        this.filtersService.handleFilterChange(true);
    }

    private isUnchangedDate(input: string, model: DateInputModel): boolean {
        return !!input && !!model.date && new Date(input).getTime() === model.date.getTime();
    }

    async onTargetStartFromChanged(input: string) {
        if (this.isUnchangedDate(input, this.targetStartFromFilterModel)) return;
        this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("targetStartFrom", input);
        await this.storeAndSetTargetStartFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onTargetStartToChanged(input: string) {
        if (this.isUnchangedDate(input, this.targetStartToFilterModel)) return;
        this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("targetStartTo", input);
        await this.storeAndSetTargetStartFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onTargetStartDueOnChanged(input: string) {
        if (this.isUnchangedDate(input, this.targetStartDueOnFilterModel)) return;
        this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("targetStartDueOn", input);
        await this.storeAndSetTargetStartFilter();
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetTargetStartFilter() {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetStart"], this.targetStartFilterModel.selection.value);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetStartFrom"], this.targetStartFromFilterModel.text);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetStartTo"], this.targetStartToFilterModel.text);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetStartDueOn"], this.targetStartDueOnFilterModel.text);
        await this.setTargetStartFilter(this.targetStartFilterModel.selection.value as string);
    }

    async setTargetStartFilter(selectedOption: string) {
        const selectedValue =
            this.targetStartFilterModel.applicableOptions.find((option) => option.value === selectedOption) ??
            this.targetStartFilterModel.applicableOptions[0];
        const value = selectedValue.value as string;
        const from = value === "dueOn" ? this.targetStartDueOnFilterModel.text : this.targetStartFromFilterModel.text;
        const to = this.targetStartToFilterModel.text;
        const filter = this.targetStartFilterGeneratorService.generateFilter(value, from, to);
        if (!filter) {
            this.filtersService.activeFilters.delete("TargetStart");
            this.filtersService.activeFiltersForPrint.delete("TargetStart");
        } else {
            this.filtersService.activeFilters.set("TargetStart", filter);
            this.filtersService.activeFiltersForPrint.set(
                "TargetStart",
                this.targetStartFilterGeneratorService.getPrintDescription(this.targetStartFilterModel.label, selectedValue, from, to),
            );
        }
    }

    async onTargetEndChanged(selectedOption: string) {
        this.targetEndFilterModel.selection =
            this.targetEndFilterModel.applicableOptions.find((option) => option.value === selectedOption) ??
            this.targetEndFilterModel.applicableOptions[0];
        this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.handleFavoriteFilterOnSelectionChange("targetEnd", selectedOption);
        await this.storeAndSetTargetEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onTargetEndFromChanged(input: string) {
        if (this.isUnchangedDate(input, this.targetEndFromFilterModel)) return;
        this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("targetEndFrom", input);
        await this.storeAndSetTargetEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onTargetEndToChanged(input: string) {
        if (this.isUnchangedDate(input, this.targetEndToFilterModel)) return;
        this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("targetEndTo", input);
        await this.storeAndSetTargetEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onTargetEndDueOnChanged(input: string) {
        if (this.isUnchangedDate(input, this.targetEndDueOnFilterModel)) return;
        this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("targetEndDueOn", input);
        await this.storeAndSetTargetEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetTargetEndFilter() {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetEnd"], this.targetEndFilterModel.selection.value);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetEndFrom"], this.targetEndFromFilterModel.text);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetEndTo"], this.targetEndToFilterModel.text);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["TargetEndDueOn"], this.targetEndDueOnFilterModel.text);
        await this.setTargetEndFilter(this.targetEndFilterModel.selection.value as string);
    }

    async setTargetEndFilter(selectedOption: string) {
        const selectedValue =
            this.targetEndFilterModel.applicableOptions.find((option) => option.value === selectedOption) ??
            this.targetEndFilterModel.applicableOptions[0];
        const value = selectedValue.value as string;
        const from = value === "dueOn" ? this.targetEndDueOnFilterModel.text : this.targetEndFromFilterModel.text;
        const to = this.targetEndToFilterModel.text;
        const filter = this.targetEndFilterGeneratorService.generateFilter(value, from, to);
        if (!filter) {
            this.filtersService.activeFilters.delete("TargetEnd");
            this.filtersService.activeFiltersForPrint.delete("TargetEnd");
        } else {
            this.filtersService.activeFilters.set("TargetEnd", filter);
            this.filtersService.activeFiltersForPrint.set(
                "TargetEnd",
                this.targetEndFilterGeneratorService.getPrintDescription(this.targetEndFilterModel.label, selectedValue, from, to),
            );
        }
    }

    async onPeriodEndChanged(selectedOption: string) {
        this.periodEndFilterModel.selection =
            this.periodEndFilterModel.applicableOptions.find((option) => option.value === selectedOption) ??
            this.periodEndFilterModel.applicableOptions[0];
        this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.handleFavoriteFilterOnSelectionChange("periodEnd", selectedOption);
        await this.storeAndSetPeriodEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onPeriodEndFromChanged(input: string) {
        if (this.isUnchangedDate(input, this.periodEndFromFilterModel)) return;
        this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("periodEndFrom", input);
        await this.storeAndSetPeriodEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onPeriodEndToChanged(input: string) {
        if (this.isUnchangedDate(input, this.periodEndToFilterModel)) return;
        this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("periodEndTo", input);
        await this.storeAndSetPeriodEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async onPeriodEndDueOnChanged(input: string) {
        if (this.isUnchangedDate(input, this.periodEndDueOnFilterModel)) return;
        this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(input ?? "");
        this.handleFavoriteFilterOnSelectionChange("periodEndDueOn", input);
        await this.storeAndSetPeriodEndFilter();
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetPeriodEndFilter() {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["PeriodEnd"], this.periodEndFilterModel.selection.value);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["PeriodEndFrom"], this.periodEndFromFilterModel.text);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["PeriodEndTo"], this.periodEndToFilterModel.text);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["PeriodEndDueOn"], this.periodEndDueOnFilterModel.text);
        await this.setPeriodEndFilter(this.periodEndFilterModel.selection.value as string);
    }

    async setPeriodEndFilter(selectedOption: string) {
        const selectedValue =
            this.periodEndFilterModel.applicableOptions.find((option) => option.value === selectedOption) ??
            this.periodEndFilterModel.applicableOptions[0];
        const value = selectedValue.value as string;
        const from = value === "dueOn" ? this.periodEndDueOnFilterModel.text : this.periodEndFromFilterModel.text;
        const to = this.periodEndToFilterModel.text;
        const filter = this.periodEndFilterGeneratorService.generateFilter(value, from, to);
        if (!filter) {
            this.filtersService.activeFilters.delete("PeriodEnd");
            this.filtersService.activeFiltersForPrint.delete("PeriodEnd");
        } else {
            this.filtersService.activeFilters.set("PeriodEnd", filter);
            this.filtersService.activeFiltersForPrint.set(
                "PeriodEnd",
                this.periodEndFilterGeneratorService.getPrintDescription(this.periodEndFilterModel.label, selectedValue, from, to),
            );
        }
    }

    async onAssigneeChanged(model: MultiSelectModel) {
        const newModel = this.assigneesFilterGeneratorService.updateModel(model.id, model.selection);
        this.handleFavoriteFilterOnSelectionChange("assignees", newModel.selection);
        await this.storeAndSetAssigneeFilter(newModel);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetAssigneeFilter(model: MultiSelectModel) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Assignees", model.id], model.selection);
        this.setAssigneeFilter(model);
    }

    setAssigneeFilter(model: MultiSelectModel) {
        const selectedValues = model.selection?.map((option) => option && (option.value as string)) ?? [];
        const filter = this.assigneesFilterGeneratorService.generateFilter(selectedValues);
        const contactAssigneeKey = `ContactAssignees_${model.id}`;
        if (!filter) {
            this.filtersService.activeFilters.delete(contactAssigneeKey);
            this.filtersService.activeFiltersForPrint.delete(contactAssigneeKey);
        } else {
            this.filtersService.activeFilters.set(contactAssigneeKey, filter);
            this.filtersService.activeFiltersForPrint.set(
                contactAssigneeKey,
                this.assigneesFilterGeneratorService.getPrintDescription(model.label, selectedValues),
            );
        }
    }

    async clearAllFilters() {
        this.isBulkFilterUpdate = true;
        try {
            await this.clearAllFiltersCore();
        } finally {
            this.isBulkFilterUpdate = false;
        }
        this.filtersService.handleFilterChange(true);
    }

    private async clearAllFiltersCore() {
        const viewType = this.cardViewService.currentViewType;
        this.alertsFilterModel = this.alertsFilterGeneratorService.generateModel([], viewType);
        this.priorityFilterModel = this.priorityFilterGeneratorService.generateModel([]);
        this.usersFilterGeneratorService.loadModel([]);
        this.jobsCurrentlyResponsibleFilterGeneratorService.loadModel([]);
        this.jobsPartnerFilterGeneratorService.loadModel([]);
        this.jobsManagerFilterGeneratorService.loadModel([]);
        this.jobsOwnerFilterGeneratorService.loadModel([]);
        // Independent model generations run concurrently instead of a sequential await chain.
        [this.jobsTypeFilterModel, this.jobsGroup1FilterModel, this.jobsGroup2FilterModel, this.group1FilterModel, this.group2FilterModel] =
            await Promise.all([
                this.jobsTypeFilterGeneratorService.generateModel([]),
                this.jobsGroupFilterGeneratorService.generateGroup1Model([]),
                this.jobsGroupFilterGeneratorService.generateGroup2Model([]),
                this.ifirmGroupFilterGeneratorService.generateGroup1Model([]),
                this.ifirmGroupFilterGeneratorService.generateGroup2Model([]),
            ]);
        this.targetStartFilterModel = this.targetStartFilterGeneratorService.generateModel();
        this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.targetEndFilterModel = this.targetEndFilterGeneratorService.generateModel();
        this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.periodEndFilterModel = this.periodEndFilterGeneratorService.generateModel();
        this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.assigneesFilterGeneratorService.resetCache();

        // Independent filter writes run concurrently instead of a sequential await chain.
        await Promise.all([
            this.storeAndSetAlertsFilter(this.alertsFilterModel.selection),
            this.storeAndSetPriorityFilter(this.priorityFilterModel.selection),
            this.storeAndSetJobsTypeFilter(this.jobsTypeFilterModel.selection),
            this.storeAndSetUsersFilter([]),
            this.storeAndSetCurrentlyResponsibleFilter([]),
            this.storeAndSetJobPartnerFilter([]),
            this.storeAndSetJobManagerFilter([]),
            this.storeAndSetJobOwnerFilter([]),
            this.jobsGroup1FilterModel ? this.storeAndSetJobsGroup1Filter(this.jobsGroup1FilterModel.selection) : Promise.resolve(),
            this.jobsGroup2FilterModel ? this.storeAndSetJobsGroup2Filter(this.jobsGroup2FilterModel.selection) : Promise.resolve(),
            this.storeAndSetGroup1Filter(this.group1FilterModel.selection),
            this.storeAndSetGroup2Filter(this.group2FilterModel.selection),
            this.storeAndSetTargetStartFilter(),
            this.storeAndSetTargetEndFilter(),
            this.storeAndSetPeriodEndFilter(),
        ]);
        this.assigneesFilterGeneratorService.resetCache();
        this.assigneesFilterGeneratorService.models().forEach((model) => {
            model.selection = [];
            this.storeAndSetAssigneeFilter(model);
        });

        this.isFavoriteFilterEditMode = false;
        this.favoritesFilters.forEach((f) => (f.active = false));
        localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(this.favoritesFilters));
        this.clearCurrentFavoriteFilter();
    }

    open() {
        if (this.hasPendingFavoriteFilterChanges) {
            this.showPendingChangesPopup(() => {
                this.isOpen = false;
                this.modalCoordinationService.closeAllModals();
            });
            return;
        }
        this.isOpen = !this.isOpen;
        this.isFavoriteFilterEditMode = false;
        this.writeState("isExpanded", false);
        if (this.isOpen) {
            // Set active modal to filter first, then close others to avoid race condition
            this.modalCoordinationService.setActiveModal("filter");
        } else {
            // Close all modals when closing filter
            this.modalCoordinationService.closeAllModals();
        }
    }

    close(reason?: "overlay" | "esc" | "closeButton") {
        if (reason === "overlay") {
            return;
        }
        if (reason === "esc") {
            if (this.hasPendingFavoriteFilterChanges) {
                this.showPendingChangesPopup(() => {
                    this.isOpen = false;
                    this.modalCoordinationService.closeAllModals();
                });
                return;
            }
        }
        this.isOpen = false;
        this.modalCoordinationService.closeAllModals();
    }

    saveAccordionState(key: string, isOpen: boolean) {
        LocalStorageService.setObject(`Accordion_States_${this.authService.currentUser()?.sub}`, [key], isOpen);
    }

    getAccordionState(key: string, defaultValue = true): boolean {
        const item = localStorage.getItem(`Accordion_States_${this.authService.currentUser()?.sub}`);

        let value: any = defaultValue;
        if (item) {
            const parsed = JSON.parse(item);
            if (parsed && typeof parsed === "object" && key in parsed) {
                value = parsed[key];
            }
        }
        return value;
    }

    onAccordionOpen(event: boolean, key: string): void {
        if (event) {
            this.accordionStates[key] = true;
            this.saveAccordionState(key, true);
        } else {
            this.accordionStates[key] = false;
            this.saveAccordionState(key, false);
        }
    }

    onAccordionClose(key: string): void {
        this.accordionStates[key] = false;
        this.saveAccordionState(key, false);
    }

    onPillClose(model: any, sel: any, changeHandler: (selection: any) => void) {
        if (!model || !Array.isArray(model.selection)) return;
        model.selection = model.selection.filter((s: any) => s.value !== sel.value);
        changeHandler.name === "onAssigneeChanged" ? changeHandler.call(this, model) : changeHandler.call(this, model.selection);
    }

    onExpandButtonToggle(): void {
        this.writeState("isExpanded", !this.isExpanded);
    }
    onVisiblePillsChange(event: { morePillsCount: number }): void {
        this.writeState("morePillsCount", event.morePillsCount);
    }

    getFavoritesFiltersKey(currentDashboardId: string, userId?: string): string {
        return `FavoritesFilters_jobs_${currentDashboardId}_${userId}`;
    }

    loadFavoriteFilters() {
        this.saveDefaultFavoriteFilter();
        this.favoritesFilters = this.getFavoritesFilters();
        const activeFavoritesFilters = this.favoritesFilters.find((f) => f.active);
        this.currentFavoriteFilter = activeFavoritesFilters ?? {
            id: "",
            name: "",
            active: false,
            selections: {},
        };
    }

    applyActiveFavoriteOnLoad() {
        const activeFavorite = this.favoritesFilters.find((f) => f.active);
        if (activeFavorite) {
            this.applyFavoriteFilter(activeFavorite);
        }
    }

    async applyFavoriteFilter(favorite: FavoriteFilters) {
        this.isBulkFilterUpdate = true;
        try {
            await this.applyFavoriteFilterCore(favorite);
        } finally {
            this.isBulkFilterUpdate = false;
        }
        // 3. Notify filter change
        this.filtersService.handleFilterChange();
        this.currentFavoriteFilter = favorite;
    }

    private async applyFavoriteFilterCore(favorite: FavoriteFilters) {
        // 1. Clear all selections and active filters
        this.alertsFilterModel.selection = [];
        this.setAlertsFilter([]);
        this.priorityFilterModel.selection = [];
        this.setPriorityFilter([]);
        this.jobsTypeFilterModel.selection = [];
        this.usersFilterGeneratorService.updateSelection([]);
        this.jobsCurrentlyResponsibleFilterGeneratorService.updateSelection([]);
        this.jobsPartnerFilterGeneratorService.updateSelection([]);
        this.jobsManagerFilterGeneratorService.updateSelection([]);
        this.jobsOwnerFilterGeneratorService.updateSelection([]);
        if (this.jobsGroup1FilterModel) this.jobsGroup1FilterModel.selection = [];
        if (this.jobsGroup2FilterModel) this.jobsGroup2FilterModel.selection = [];
        this.group1FilterModel.selection = [];
        this.group2FilterModel.selection = [];
        if (Array.isArray(this.assigneesFilterGeneratorService.models())) {
            this.assigneesFilterGeneratorService.models().forEach((model, idx) => {
                this.assigneesFilterGeneratorService.updateSelection(idx, []);
                model.selection = [];
                this.setAssigneeFilter(model);
            });
        }
        this.targetStartFilterModel.selection = this.targetStartFilterModel.applicableOptions[0];
        this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.targetEndFilterModel.selection = this.targetEndFilterModel.applicableOptions[0];
        this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        this.periodEndFilterModel.selection = this.periodEndFilterModel.applicableOptions[0];
        this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
        this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
        this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
        // Independent filter recomputations run concurrently instead of a sequential await chain.
        await Promise.all([
            this.setJobsTypeFilter([]),
            this.setJobsGroup1Filter([]),
            this.setJobsGroup2Filter([]),
            this.setGroup1Filter([]),
            this.setGroup2Filter([]),
            this.setTargetStartFilter(this.targetStartFilterModel.selection.value as string),
            this.setTargetEndFilter(this.targetEndFilterModel.selection.value as string),
            this.setPeriodEndFilter(this.periodEndFilterModel.selection.value as string),
        ]);

        // 2. Apply favorite selections and update active filters
        if (favorite.selections.alerts) {
            this.alertsFilterModel.selection = favorite.selections.alerts;
            this.setAlertsFilter(this.alertsFilterModel.selection);
        }
        if (favorite.selections.priority) {
            this.priorityFilterModel.selection = favorite.selections.priority;
            this.setPriorityFilter(this.priorityFilterModel.selection);
        }
        // Independent filter recomputations are collected and run concurrently below instead of awaited one by one.
        const applyFilterPromises: Promise<unknown>[] = [];
        if (favorite.selections.jobsType) {
            this.jobsTypeFilterModel.selection = favorite.selections.jobsType;
            applyFilterPromises.push(this.setJobsTypeFilter(this.jobsTypeFilterModel.selection));
        }
        if (favorite.selections.users) {
            this.usersFilterGeneratorService.updateSelection(favorite.selections.users);
        }
        if (favorite.selections.currentlyResponsible) {
            this.jobsCurrentlyResponsibleFilterGeneratorService.updateSelection(favorite.selections.currentlyResponsible);
        }
        if (favorite.selections.jobPartner) {
            this.jobsPartnerFilterGeneratorService.updateSelection(favorite.selections.jobPartner);
        }
        if (favorite.selections.jobManager) {
            this.jobsManagerFilterGeneratorService.updateSelection(favorite.selections.jobManager);
        }
        if (favorite.selections.jobOwner) {
            this.jobsOwnerFilterGeneratorService.updateSelection(favorite.selections.jobOwner);
        }
        if (this.jobsGroup1FilterModel && favorite.selections.jobsGroup1) {
            this.jobsGroup1FilterModel.selection = favorite.selections.jobsGroup1;
            applyFilterPromises.push(this.setJobsGroup1Filter(this.jobsGroup1FilterModel.selection));
        }
        if (this.jobsGroup2FilterModel && favorite.selections.jobsGroup2) {
            this.jobsGroup2FilterModel.selection = favorite.selections.jobsGroup2;
            applyFilterPromises.push(this.setJobsGroup2Filter(this.jobsGroup2FilterModel.selection));
        }
        if (favorite.selections.group1) {
            this.group1FilterModel.selection = favorite.selections.group1;
            applyFilterPromises.push(this.setGroup1Filter(this.group1FilterModel.selection));
        }
        if (favorite.selections.group2) {
            this.group2FilterModel.selection = favorite.selections.group2;
            applyFilterPromises.push(this.setGroup2Filter(this.group2FilterModel.selection));
        }
        if (favorite.selections.assignees && Array.isArray(favorite.selections.assignees)) {
            favorite.selections.assignees.forEach((sel, idx) => {
                if (this.assigneesFilterGeneratorService.models()[idx]) {
                    const model = this.assigneesFilterGeneratorService.updateSelection(idx, sel);
                    this.setAssigneeFilter(model);
                }
            });
        }
        if (favorite.selections.targetStart) {
            this.targetStartFilterModel.selection =
                this.targetStartFilterModel.applicableOptions.find((option) => option.value === favorite.selections.targetStart) ||
                this.targetStartFilterModel.applicableOptions[0];
        }
        if (favorite.selections.targetStartFrom) {
            this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(favorite.selections.targetStartFrom);
        }
        if (favorite.selections.targetStartTo) {
            this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel(favorite.selections.targetStartTo);
        }
        if (favorite.selections.targetStartDueOn) {
            this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(favorite.selections.targetStartDueOn);
        }
        applyFilterPromises.push(this.setTargetStartFilter(this.targetStartFilterModel.selection.value as string));
        if (favorite.selections.targetEnd) {
            this.targetEndFilterModel.selection =
                this.targetEndFilterModel.applicableOptions.find((option) => option.value === favorite.selections.targetEnd) ||
                this.targetEndFilterModel.applicableOptions[0];
        }
        if (favorite.selections.targetEndFrom) {
            this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(favorite.selections.targetEndFrom);
        }
        if (favorite.selections.targetEndTo) {
            this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(favorite.selections.targetEndTo);
        }
        if (favorite.selections.targetEndDueOn) {
            this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(favorite.selections.targetEndDueOn);
        }
        applyFilterPromises.push(this.setTargetEndFilter(this.targetEndFilterModel.selection.value as string));
        if (favorite.selections.periodEnd) {
            this.periodEndFilterModel.selection =
                this.periodEndFilterModel.applicableOptions.find((option) => option.value === favorite.selections.periodEnd) ||
                this.periodEndFilterModel.applicableOptions[0];
        }
        if (favorite.selections.periodEndFrom) {
            this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(favorite.selections.periodEndFrom);
        }
        if (favorite.selections.periodEndTo) {
            this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(favorite.selections.periodEndTo);
        }
        if (favorite.selections.periodEndDueOn) {
            this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(favorite.selections.periodEndDueOn);
        }
        applyFilterPromises.push(this.setPeriodEndFilter(this.periodEndFilterModel.selection.value as string));
        await Promise.all(applyFilterPromises);

        // Special handling for "Assigned to me"
        if (favorite.id === "assigned-to-me") {
            const currentUserId = this.authService.currentUser()?.sub;
            const currentIFirmUserId = this.authService.currentUser()?.ifirm_userid;

            if (this.usersFilterModel) {
                const userOption = this.usersFilterModel?.allOptions?.find((opt) => {
                    try {
                        const valueObj = typeof opt.value === "string" ? JSON.parse(opt.value) : opt.value;
                        return valueObj && (String(valueObj) === currentUserId || String(valueObj) === currentIFirmUserId);
                    } catch {
                        return false;
                    }
                });
                if (userOption) {
                    this.usersFilterGeneratorService.updateSelection([userOption]);

                    favorite.selections.users = [userOption];
                    // Save to localStorage
                    const existingFilters: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
                    const idx = existingFilters.findIndex((f) => f.id === "assigned-to-me");
                    if (idx !== -1) {
                        existingFilters[idx].selections.users = [userOption];
                        localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(existingFilters));
                    }
                }
            }
        }
    }

    async toggleFavorite(model) {
        if (this.isFavoriteFilterEditMode && !this.hasPendingFavoriteFilterChanges) {
            this.isFavoriteFilterEditMode = false;
        }
        if (this.isFavoriteFilterEditMode && this.hasPendingFavoriteFilterChanges) {
            this.pendingFavoriteToApply = model;
            this.bsModalRef = this.modalService.show(PendingChangesPopupComponent, {
                initialState: {
                    onDiscard: () => {
                        this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
                        this.isFavoriteFilterEditMode = false;
                        // Apply the new favorite filter directly instead of reverting to current
                        const newFavorite = this.pendingFavoriteToApply;
                        this.pendingFavoriteToApply = null;
                        this.currentFavoriteFilter = newFavorite;
                        this.toggleFavorite(newFavorite);
                    },
                    onKeepEditing: () => {
                        // Clear pending favorite
                        this.pendingFavoriteToApply = null;
                    },
                },
                class: "custom-modal-rename-dashboard",
            });
            // Pass the modal ref to the component after it's created
            if (this.bsModalRef.content) {
                this.bsModalRef.content.bsModalRef = this.bsModalRef;
            }
            return;
        }

        if (this.isFavoriteFilterEditMode && !this.hasPendingFavoriteFilterChanges) {
            this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
        }

        const existingFilters: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
        const index = existingFilters.findIndex((f) => f.id === model.id);

        if (index !== -1) {
            existingFilters.forEach((f) => (f.active = false));

            existingFilters[index].active = !model.active;
            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(existingFilters));
            this.favoritesFilters = this.getFavoritesFilters();

            if (existingFilters[index].active) {
                await this.applyFavoriteFilter(existingFilters[index]);
            } else {
                this.clearAllFilters();
                this.currentFavoriteFilter = {
                    id: "",
                    name: "",
                    active: false,
                    selections: {},
                };
            }
        }
    }

    saveFavoritesFilter(filterName: string) {
        try {
            const existingFilters = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
            const currentFilterId = this.modalActionType === "rename" || this.isFavoriteFilterEditMode ? this.currentFavoriteFilter?.id : null;
            const hasDuplicateName = existingFilters.some(
                (filter: FavoriteFilters) =>
                    filter.name.toLowerCase() === filterName.toLowerCase() && (!currentFilterId || filter.id !== currentFilterId),
            );
            if (hasDuplicateName) {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "FILTER-SAVE-ERROR",
                    messageCode: "duplicateFavoriteFilterName",
                    additionInfo: this.translateService.instant("NotifyMessages.DuplicateFavoriteFilterNameContent"),
                });
                return;
            }

            if (this.modalActionType === "rename" && this.currentFavoriteFilter?.id) {
                const idx = existingFilters.findIndex((f) => f.id === this.currentFavoriteFilter?.id);
                if (idx !== -1) {
                    existingFilters[idx].name = filterName;
                }
            } else if (this.isFavoriteFilterEditMode && this.currentFavoriteFilter?.id) {
                const idx = existingFilters.findIndex((f) => f.id === this.currentFavoriteFilter?.id);
                if (idx !== -1) {
                    existingFilters[idx].name = filterName;
                    existingFilters[idx].selections = {
                        alerts: this.alertsFilterModel?.selection ?? [],
                        priority: this.priorityFilterModel?.selection ?? [],
                        jobsType: this.jobsTypeFilterModel?.selection ?? [],
                        jobsGroup1: this.jobsGroup1FilterModel?.selection ?? [],
                        jobsGroup2: this.jobsGroup2FilterModel?.selection ?? [],
                        group1: this.group1FilterModel?.selection ?? [],
                        group2: this.group2FilterModel?.selection ?? [],
                        assignees: this.assigneesFilterGeneratorService.models().map((m) => m.selection) ?? [],
                        targetStart: (this.targetStartFilterModel?.selection?.value as string) ?? "all",
                        targetStartFrom: this.targetStartFromFilterModel?.text ?? "",
                        targetStartTo: this.targetStartToFilterModel?.text ?? "",
                        targetStartDueOn: this.targetStartDueOnFilterModel?.text ?? "",
                        targetEnd: (this.targetEndFilterModel?.selection?.value as string) ?? "all",
                        targetEndFrom: this.targetEndFromFilterModel?.text ?? "",
                        targetEndTo: this.targetEndToFilterModel?.text ?? "",
                        targetEndDueOn: this.targetEndDueOnFilterModel?.text ?? "",
                        periodEnd: (this.periodEndFilterModel?.selection?.value as string) ?? "all",
                        periodEndFrom: this.periodEndFromFilterModel?.text ?? "",
                        periodEndTo: this.periodEndToFilterModel?.text ?? "",
                        periodEndDueOn: this.periodEndDueOnFilterModel?.text ?? "",
                        users: this.usersFilterModel?.selection ?? [],
                        currentlyResponsible: this.currentlyResponsibleFilterModel?.selection ?? [],
                        jobPartner: this.jobPartnerFilterModel?.selection ?? [],
                        jobManager: this.jobManagerFilterModel?.selection ?? [],
                        jobOwner: this.jobOwnerFilterModel?.selection ?? [],
                    };
                    this.currentFavoriteFilter = existingFilters[idx];
                    this.trackSaveFilterAnalytics("Update", existingFilters[idx].selections);
                }
                this.isFavoriteFilterEditMode = false;
                this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
            } else {
                const filter: FavoriteFilters = {
                    id: Date.now().toString(),
                    name: filterName,
                    active: true,
                    selections: {
                        alerts: this.alertsFilterModel?.selection ?? [],
                        priority: this.priorityFilterModel?.selection ?? [],
                        jobsType: this.jobsTypeFilterModel?.selection ?? [],
                        jobsGroup1: this.jobsGroup1FilterModel?.selection ?? [],
                        jobsGroup2: this.jobsGroup2FilterModel?.selection ?? [],
                        group1: this.group1FilterModel?.selection ?? [],
                        group2: this.group2FilterModel?.selection ?? [],
                        assignees: this.assigneesFilterGeneratorService.models().map((m) => m.selection) ?? [],
                        targetStart: (this.targetStartFilterModel?.selection?.value as string) ?? "all",
                        targetStartFrom: this.targetStartFromFilterModel?.text ?? "",
                        targetStartTo: this.targetStartToFilterModel?.text ?? "",
                        targetStartDueOn: this.targetStartDueOnFilterModel?.text ?? "",
                        targetEnd: (this.targetEndFilterModel?.selection?.value as string) ?? "all",
                        targetEndFrom: this.targetEndFromFilterModel?.text ?? "",
                        targetEndTo: this.targetEndToFilterModel?.text ?? "",
                        targetEndDueOn: this.targetEndDueOnFilterModel?.text ?? "",
                        periodEnd: (this.periodEndFilterModel?.selection?.value as string) ?? "all",
                        periodEndFrom: this.periodEndFromFilterModel?.text ?? "",
                        periodEndTo: this.periodEndToFilterModel?.text ?? "",
                        periodEndDueOn: this.periodEndDueOnFilterModel?.text ?? "",
                        users: this.usersFilterModel?.selection ?? [],
                        currentlyResponsible: this.currentlyResponsibleFilterModel?.selection ?? [],
                        jobPartner: this.jobPartnerFilterModel?.selection ?? [],
                        jobManager: this.jobManagerFilterModel?.selection ?? [],
                        jobOwner: this.jobOwnerFilterModel?.selection ?? [],
                    },
                };
                existingFilters.push(filter);
                this.currentFavoriteFilter = filter;
                this.trackSaveFilterAnalytics("Create", filter.selections);
                this.isFavoriteFilterEditMode = false;
                this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
            }

            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(existingFilters));
            this.favoritesFilters = this.getFavoritesFilters();
            this.bsModalRef?.hide();
        } catch {
            this.bsModalRef?.hide();
            this.responseErrorHandlerService.showPopup({
                typeLayout: "FILTER-SAVE-ERROR",
                messageCode: "filterSaveError",
                additionInfo: this.translateService.instant("NotifyMessages.FavoritesFiltersSaveFailContent"),
            });
        }
    }

    saveDefaultFavoriteFilter() {
        const favorites: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
        const exists = favorites.some((f) => f.id === "assigned-to-me");
        if (!exists) {
            favorites.unshift({
                id: "assigned-to-me",
                name: "Assigned to me",
                active: false,
                selections: {},
            });

            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(favorites));
        }
    }

    enableFavoriteFilterEditMode(isEnabled: boolean) {
        if (this.hasPendingFavoriteFilterChanges) {
            this.showPendingChangesPopup(() => {
                this.helpersService.favouriteFiltersEditModeEmitter.next(isEnabled);
            });
        } else {
            this.helpersService.favouriteFiltersEditModeEmitter.next(isEnabled);
        }
    }

    handleFilterEditMode(isEnabled: boolean) {
        this.isFavoriteFilterEditMode = isEnabled;
    }

    deleteFavoriteFilter(filterId: string): void {
        try {
            const existingFilters: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
            const updatedFilters = existingFilters.filter((f) => f.id !== filterId);
            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(updatedFilters));
            this.favoritesFilters = this.getFavoritesFilters();
            this.bsModalRef?.hide();
            this.clearCurrentFavoriteFilter();
            this.clearAllFilters();
        } catch {
            this.bsModalRef?.hide();
        }
    }

    clearCurrentFavoriteFilter() {
        this.currentFavoriteFilter = {
            id: "",
            name: "",
            active: false,
            selections: {},
        };
    }

    async handleFavoriteFilterOnSelectionChange(
        filterType: string,
        selectedOptions: Option[] | Option | string | boolean | { april30th: boolean; june15th: boolean },
        resetFavorite = true,
    ) {
        if (!this.isFavoriteFilterEditMode && this.currentFavoriteFilter && this.currentFavoriteFilter.id.length > 0) {
            const fav = this.currentFavoriteFilter.selections;

            // 1. Set all filter models from favorite filter selections
            this.applyFavoriteSelectionsToModels(fav);

            // 2. Overwrite the changed filter model with the user's new selection
            switch (filterType) {
                case "alerts":
                    this.alertsFilterModel.selection = selectedOptions as Option[];
                    break;
                case "priority":
                    this.priorityFilterModel.selection = selectedOptions as Option[];
                    break;
                case "jobsType":
                    this.jobsTypeFilterModel.selection = selectedOptions as Option[];
                    break;
                case "jobsGroup1":
                    this.jobsGroup1FilterModel.selection = selectedOptions as Option[];
                    break;
                case "jobsGroup2":
                    this.jobsGroup2FilterModel.selection = selectedOptions as Option[];
                    break;
                case "group1":
                    this.group1FilterModel.selection = selectedOptions as Option[];
                    break;
                case "group2":
                    this.group2FilterModel.selection = selectedOptions as Option[];
                    break;
                case "targetStart":
                    this.targetStartFilterModel.selection =
                        this.targetStartFilterModel.applicableOptions.find((option) => option.value === (selectedOptions as string)) ??
                        this.targetStartFilterModel.applicableOptions[0];
                    this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
                    this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
                    this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
                    break;
                case "targetStartFrom":
                    this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "targetStartTo":
                    this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "targetStartDueOn":
                    this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "targetEnd":
                    this.targetEndFilterModel.selection =
                        this.targetEndFilterModel.applicableOptions.find((option) => option.value === (selectedOptions as string)) ??
                        this.targetEndFilterModel.applicableOptions[0];
                    this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
                    this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
                    this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
                    break;
                case "targetEndFrom":
                    this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "targetEndTo":
                    this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "targetEndDueOn":
                    this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "periodEnd":
                    this.periodEndFilterModel.selection =
                        this.periodEndFilterModel.applicableOptions.find((option) => option.value === (selectedOptions as string)) ??
                        this.periodEndFilterModel.applicableOptions[0];
                    this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel("");
                    this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel("");
                    this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel("");
                    break;
                case "periodEndFrom":
                    this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "periodEndTo":
                    this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "periodEndDueOn":
                    this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel((selectedOptions as string) ?? "");
                    break;
                case "users":
                    this.usersFilterGeneratorService.updateSelection(selectedOptions as Option[]);
                    break;
                case "currentlyResponsible":
                    this.jobsCurrentlyResponsibleFilterGeneratorService.updateSelection(selectedOptions as Option[]);
                    break;
                case "jobPartner":
                    this.jobsPartnerFilterGeneratorService.updateSelection(selectedOptions as Option[]);
                    break;
                case "jobManager":
                    this.jobsManagerFilterGeneratorService.updateSelection(selectedOptions as Option[]);
                    break;
                case "jobOwner":
                    this.jobsOwnerFilterGeneratorService.updateSelection(selectedOptions as Option[]);
                    break;
            }

            // 3. Store all filter selections in local cache
            await this.storeAllFilterSelections();

            // 4. Deactivate favorite filter and clear currentFavoriteFilter
            if (resetFavorite) {
                this.favoritesFilters.forEach((f) => (f.active = false));
                localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(this.favoritesFilters));
                this.clearCurrentFavoriteFilter();
            }

            // 5. Notify filter change
            this.filtersService.handleFilterChange();
        }
    }

    private applyFavoriteSelectionsToModels(fav: FavoriteFilters["selections"]) {
        this.alertsFilterModel.selection = fav.alerts ?? [];
        this.priorityFilterModel.selection = fav.priority ?? [];
        this.jobsTypeFilterModel.selection = fav.jobsType ?? [];
        this.usersFilterGeneratorService.updateSelection(fav.users ?? []);
        this.jobsCurrentlyResponsibleFilterGeneratorService.updateSelection(fav.currentlyResponsible ?? []);
        this.jobsPartnerFilterGeneratorService.updateSelection(fav.jobPartner ?? []);
        this.jobsManagerFilterGeneratorService.updateSelection(fav.jobManager ?? []);
        this.jobsOwnerFilterGeneratorService.updateSelection(fav.jobOwner ?? []);
        if (this.jobsGroup1FilterModel) this.jobsGroup1FilterModel.selection = fav.jobsGroup1 ?? [];
        if (this.jobsGroup2FilterModel) this.jobsGroup2FilterModel.selection = fav.jobsGroup2 ?? [];
        this.group1FilterModel.selection = fav.group1 ?? [];
        this.group2FilterModel.selection = fav.group2 ?? [];

        if (Array.isArray(this.assigneesFilterGeneratorService.models()) && Array.isArray(fav.assignees)) {
            this.assigneesFilterGeneratorService.models().forEach((model, idx) => {
                const favAssignees = fav.assignees?.[idx] ?? [];
                const modelSelection = model.selection ?? [];
                if (modelSelection.length > 0 && favAssignees.length === 0) {
                    // Keep model.selection as is
                    model.selection = modelSelection;
                } else if (modelSelection.length > 0 && favAssignees.length > 0) {
                    // Merge both, avoiding duplicates by value
                    const merged = [
                        ...modelSelection,
                        ...favAssignees.filter((favOpt) => !modelSelection.some((selOpt) => selOpt.value === favOpt.value)),
                    ];
                    model.selection = merged;
                } else if (modelSelection.length === 0 && favAssignees.length > 0 && this.currentFavoriteFilter?.id != "assigned-to-me") {
                    // Set fav.assignees to model.selection
                    model.selection = favAssignees;
                } else {
                    // Both empty, keep as empty
                    model.selection = [];
                }
            });
        }

        this.targetStartFilterModel.selection =
            this.targetStartFilterModel.applicableOptions.find((option) => option.value === fav.targetStart) ??
            this.targetStartFilterModel.applicableOptions[0];
        this.targetStartFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(fav.targetStartFrom ?? "");
        this.targetStartToFilterModel = this.targetStartToFilterGeneratorService.generateModel(fav.targetStartTo ?? "");
        this.targetStartDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(fav.targetStartDueOn ?? "");
        this.targetEndFilterModel.selection =
            this.targetEndFilterModel.applicableOptions.find((option) => option.value === fav.targetEnd) ??
            this.targetEndFilterModel.applicableOptions[0];
        this.targetEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(fav.targetEndFrom ?? "");
        this.targetEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(fav.targetEndTo ?? "");
        this.targetEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(fav.targetEndDueOn ?? "");
        this.periodEndFilterModel.selection =
            this.periodEndFilterModel.applicableOptions.find((option) => option.value === fav.periodEnd) ??
            this.periodEndFilterModel.applicableOptions[0];
        this.periodEndFromFilterModel = this.targetStartFromFilterGeneratorService.generateModel(fav.periodEndFrom ?? "");
        this.periodEndToFilterModel = this.targetStartToFilterGeneratorService.generateModel(fav.periodEndTo ?? "");
        this.periodEndDueOnFilterModel = this.dueOnFilterGeneratorService.generateModel(fav.periodEndDueOn ?? "");
    }

    private async storeAllFilterSelections() {
        await this.storeAndSetAlertsFilter(this.alertsFilterModel.selection);
        await this.storeAndSetPriorityFilter(this.priorityFilterModel.selection);
        await this.storeAndSetJobsTypeFilter(this.jobsTypeFilterModel.selection);
        await this.storeAndSetUsersFilter(this.usersFilterModel.selection);
        await this.storeAndSetCurrentlyResponsibleFilter(this.currentlyResponsibleFilterModel.selection);
        await this.storeAndSetJobPartnerFilter(this.jobPartnerFilterModel.selection);
        await this.storeAndSetJobManagerFilter(this.jobManagerFilterModel.selection);
        await this.storeAndSetJobOwnerFilter(this.jobOwnerFilterModel.selection);
        await this.storeAndSetJobsGroup1Filter(this.jobsGroup1FilterModel.selection);
        await this.storeAndSetJobsGroup2Filter(this.jobsGroup2FilterModel.selection);
        await this.storeAndSetGroup1Filter(this.group1FilterModel.selection);
        await this.storeAndSetGroup2Filter(this.group2FilterModel.selection);

        if (Array.isArray(this.assigneesFilterGeneratorService.models())) {
            for (const model of this.assigneesFilterGeneratorService.models()) {
                await this.storeAndSetAssigneeFilter(model);
            }
        }

        await this.storeAndSetTargetStartFilter();
        await this.storeAndSetTargetEndFilter();
        await this.storeAndSetPeriodEndFilter();
    }

    getFavoritesFilters(): FavoriteFilters[] {
        const filtersJson = localStorage.getItem(this.favoriteFiltersLocalStorageKey);
        return filtersJson ? (JSON.parse(filtersJson) as FavoriteFilters[]) : [];
    }

    openModal(modalType: "save" | "rename" | "delete" | "pending") {
        this.modalActionType = modalType === "rename" ? "rename" : "save";
        if (modalType === "save" || modalType === "rename") {
            this.bsModalRef = this.modalService.show(this.saveFilterDialogue, {
                class: "custom-modal-rename-dashboard",
            });
        } else if (modalType === "delete") {
            this.bsModalRef = this.modalService.show(this.deleteFilterDialogue, {
                class: "custom-modal-rename-dashboard",
            });
        }
    }

    private async loadGroupEnabledState(): Promise<void> {
        const groups = await this.ifirmGroupsService.getiFirmGroups();
        this.isGroup1Enabled = groups.group1.enabled && groups.group1.group1ContactsEnabled;
        this.isGroup2Enabled = groups.group2.enabled && groups.group2.group2ContactsEnabled;
    }

    private showPendingChangesPopup(onDiscardCallback?: () => void) {
        this.bsModalRef = this.modalService.show(PendingChangesPopupComponent, {
            initialState: {
                onDiscard: () => {
                    this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
                    this.isFavoriteFilterEditMode = false;
                    if (this.currentFavoriteFilter?.id) {
                        this.applyFavoriteFilter(this.currentFavoriteFilter);
                    }
                    if (onDiscardCallback) {
                        onDiscardCallback();
                    }
                },
                onKeepEditing: () => {
                    // Just close modal, keep filter panel open
                },
            },
            class: "custom-modal-rename-dashboard",
        });
        // Pass the modal ref to the component after it's created
        if (this.bsModalRef.content) {
            this.bsModalRef.content.bsModalRef = this.bsModalRef;
        }
    }

    onAnyFilterChanged() {
        if (this.isFavoriteFilterEditMode) {
            this.helpersService.favouriteFiltersPendingChangesEmitter.next(true);
        }
    }

    private trackSaveFilterAnalytics(action: "Create" | "Update", selections: Record<string, any>): void {
        const isActiveArr = (arr: any[]) => Array.isArray(arr) && arr.length > 0;
        const isActiveDate = (val: string) => typeof val === "string" && !!val && val !== "all";
        const getOptionDisplay = (option: any): string => {
            if (!option) return "";
            if (typeof option.text === "string" && option.text) return this.translateService.instant(option.text);
            if (option.text && typeof option.text === "object") {
                return option.text["en-CA"] ?? String(option.value ?? "");
            }
            return String(option.value ?? "");
        };
        const getDateDisplay = (val: string): string => {
            if (!val || val === "all") return "None";
            if (val === "none") return "None";
            if (val === "dueOn") return "Due on";
            return "Date range";
        };
        const models = this.assigneesFilterGeneratorService.models();
        const getAssigneeSelectionByRoleId = (roleId: string): any[] => {
            const modelIndex = models.findIndex((model) => model.id === `role-${roleId}`);
            if (modelIndex === -1 || !Array.isArray(selections.assignees)) return [];
            const roleSelection = selections.assignees[modelIndex];
            return Array.isArray(roleSelection) ? roleSelection : [];
        };
        const contactPartnerSelection = getAssigneeSelectionByRoleId(PARTNER_ROLE_ID);
        const contactManagerSelection = getAssigneeSelectionByRoleId(MANAGER_ROLE_ID);

        const hasAlerts = isActiveArr(selections.alerts);
        const hasPriority = isActiveArr(selections.priority);
        const hasUsers = isActiveArr(selections.users);
        const hasCurrentlyResponsible = isActiveArr(selections.currentlyResponsible);
        const hasJobPartner = isActiveArr(selections.jobPartner);
        const hasJobManager = isActiveArr(selections.jobManager);
        const hasJobOwner = isActiveArr(selections.jobOwner);
        const hasJobsType = isActiveArr(selections.jobsType);
        const hasJobsGroup1 = isActiveArr(selections.jobsGroup1);
        const hasJobsGroup2 = isActiveArr(selections.jobsGroup2);
        const hasGroup1 = isActiveArr(selections.group1);
        const hasGroup2 = isActiveArr(selections.group2);
        const hasContactPartner = contactPartnerSelection.length > 0;
        const hasContactManager = contactManagerSelection.length > 0;
        const hasTargetStart = isActiveDate(selections.targetStart);
        const hasTargetEnd = isActiveDate(selections.targetEnd);
        const hasPeriodEnd = isActiveDate(selections.periodEnd);

        const filtersUsed: string[] = [];

        if (hasAlerts) filtersUsed.push("Alerts");
        if (hasPriority) filtersUsed.push("Priority");
        if (hasUsers) filtersUsed.push("Users");
        if (hasCurrentlyResponsible) filtersUsed.push("Currently responsible");
        if (hasJobPartner) filtersUsed.push("Job partner");
        if (hasJobManager) filtersUsed.push("Job manager");
        if (hasJobOwner) filtersUsed.push("Job owner");
        if (hasJobsType) filtersUsed.push("Job type");
        if (hasJobsGroup1) filtersUsed.push("Jobs group 1");
        if (hasJobsGroup2) filtersUsed.push("Jobs group 2");
        if (hasGroup1) filtersUsed.push("Contact group 1");
        if (hasGroup2) filtersUsed.push("Contact group 2");
        if (hasContactPartner) filtersUsed.push("Contact partner");
        if (hasContactManager) filtersUsed.push("Contact manager");
        if (hasTargetStart) filtersUsed.push(`Target start date - ${getDateDisplay(selections.targetStart)}`);
        if (hasTargetEnd) filtersUsed.push(`Target end date - ${getDateDisplay(selections.targetEnd)}`);
        if (hasPeriodEnd) filtersUsed.push(`Period ended - ${getDateDisplay(selections.periodEnd)}`);

        const analyticsPayload: Record<string, any> = {
            action,
            filters_used: filtersUsed,
        };

        if (hasAlerts) {
            analyticsPayload.alerts_cards = Array.isArray(selections.alerts)
                ? selections.alerts.map((o: any) => getOptionDisplay(o)).filter(Boolean)
                : [];
        }
        if (hasPriority) {
            analyticsPayload.priority = Array.isArray(selections.priority)
                ? selections.priority.map((o: any) => getOptionDisplay(o)).filter(Boolean)
                : [];
        }
        if (hasUsers) {
            analyticsPayload.users_count = Array.isArray(selections.users) ? selections.users.length : 0;
        }
        if (hasCurrentlyResponsible) {
            analyticsPayload.currently_responsible_count = Array.isArray(selections.currentlyResponsible)
                ? selections.currentlyResponsible.length
                : 0;
        }
        if (hasJobPartner) analyticsPayload.job_partner_count = Array.isArray(selections.jobPartner) ? selections.jobPartner.length : 0;
        if (hasJobManager) analyticsPayload.job_manager_count = Array.isArray(selections.jobManager) ? selections.jobManager.length : 0;
        if (hasJobOwner) {
            analyticsPayload.job_owner_count = Array.isArray(selections.jobOwner) ? selections.jobOwner.length : 0;
        }
        if (hasJobsType) {
            analyticsPayload.jobs_type_count = Array.isArray(selections.jobsType) ? selections.jobsType.length : 0;
            analyticsPayload.jobs_type = Array.isArray(selections.jobsType)
                ? selections.jobsType.map((o: any) => getOptionDisplay(o)).filter(Boolean)
                : [];
        }
        if (hasJobsGroup1) {
            analyticsPayload.jobs_group_1_count = Array.isArray(selections.jobsGroup1) ? selections.jobsGroup1.length : 0;
        }
        if (hasJobsGroup2) {
            analyticsPayload.jobs_group_2_count = Array.isArray(selections.jobsGroup2) ? selections.jobsGroup2.length : 0;
        }
        if (hasGroup1) {
            analyticsPayload.contact_group_1_count = Array.isArray(selections.group1) ? selections.group1.length : 0;
        }
        if (hasGroup2) {
            analyticsPayload.contact_group_2_count = Array.isArray(selections.group2) ? selections.group2.length : 0;
        }
        if (hasContactPartner) {
            analyticsPayload.contact_partner_count = contactPartnerSelection.length;
        }
        if (hasContactManager) {
            analyticsPayload.contact_manager_count = contactManagerSelection.length;
        }
        if (hasTargetStart) {
            analyticsPayload.target_start_date = getDateDisplay(selections.targetStart);
        }
        if (hasTargetEnd) {
            analyticsPayload.target_end_date = getDateDisplay(selections.targetEnd);
        }
        if (hasPeriodEnd) {
            analyticsPayload.period_ended = getDateDisplay(selections.periodEnd);
        }

        this.analyticsService.track("Save a filter", analyticsPayload);
    }
}
